<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center flex-wrap" style="gap: 0.75rem">
          <b-button
            v-if="embedded"
            variant="outline-secondary"
            size="sm"
            class="sw-back-btn"
            v-b-tooltip.hover.top="'Back to Inbox'"
            @click="$emit('back')"
          >
            <feather-icon icon="ArrowLeftIcon" size="16" />
          </b-button>
          <div>
            <h5 class="mb-0 resource-title">Retention History</h5>
            <small class="text-muted"
              >Retained (stayed) and Left (finalized) snapshots</small
            >
          </div>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="reload(false)"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--cols-6 d-flex align-items-center flex-grow-1"
          >
            <v-select
              v-model="filters.finalOutcome"
              :options="outcomeOptions"
              :clearable="false"
              :reduce="(o) => o.value"
              label="text"
              placeholder="Type"
              append-to-body
              class="filter-control filter-field-item"
              @input="reload(true)"
            />

            <v-select
              v-model="filters.parentInitiate"
              :options="parentInitiateOptions"
              :clearable="false"
              :reduce="(o) => o.value"
              label="text"
              placeholder="Parent initiate"
              append-to-body
              class="filter-control filter-field-item"
              @input="reload(true)"
            />

            <v-select
              v-model="filters.classID"
              :options="classOptions"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Class"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterClassChange"
            />

            <div class="filter-student-search filter-field-item">
              <div v-if="selectedStudent" class="selected-student-chip">
                <div class="selected-student-chip__avatar">
                  <feather-icon icon="UserIcon" size="14" />
                </div>
                <div class="selected-student-chip__body">
                  <span class="selected-student-chip__name">{{
                    selectedStudent.name
                  }}</span>
                  <span
                    v-if="selectedStudent.grNo"
                    class="meta-tag meta-tag--gr"
                  >
                    {{ selectedStudent.grNo }}
                  </span>
                </div>
                <button
                  type="button"
                  class="selected-student-chip__clear"
                  title="Clear"
                  @click="clearFilterStudent"
                >
                  <feather-icon icon="XIcon" size="13" />
                </button>
              </div>

              <div
                v-else
                class="student-search-box"
                :class="{ 'student-search-box--active': showStudentDropdown }"
              >
                <div class="student-search-input-wrap">
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                    class="student-search-icon"
                  />
                  <b-form-input
                    v-model="studentSearchQuery"
                    class="filter-search-input student-search-input"
                    placeholder="Search student..."
                    @input="onStudentSearchChange"
                    @focus="studentSearchFocused = true"
                    @blur="onStudentSearchBlur"
                  />
                  <button
                    v-if="studentSearchQuery"
                    type="button"
                    class="student-search-clear"
                    @mousedown.prevent
                    @click="clearStudentSearchQuery"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                  <b-spinner
                    v-else-if="studentSearchLoading"
                    small
                    class="student-search-spinner"
                  />
                </div>

                <transition name="student-search-fade">
                  <div
                    v-if="showStudentDropdown"
                    class="student-search-dropdown"
                  >
                    <div
                      v-if="studentSearchQuery.length < 2"
                      class="student-search-hint"
                    >
                      <feather-icon icon="InfoIcon" size="14" />
                      <span>Type at least 2 characters</span>
                    </div>
                    <template v-else>
                      <div
                        v-if="studentSearchLoading"
                        class="student-search-state"
                      >
                        <b-spinner small variant="primary" />
                        <span>Searching...</span>
                      </div>
                      <div
                        v-else-if="!filteredStudentOptions.length"
                        class="student-search-state"
                      >
                        <div class="student-search-state__icon">
                          <feather-icon icon="SearchIcon" size="18" />
                        </div>
                        <strong>No students found</strong>
                      </div>
                      <template v-else>
                        <div class="student-search-dropdown__header">
                          {{ filteredStudentOptions.length }} result{{
                            filteredStudentOptions.length === 1 ? "" : "s"
                          }}
                        </div>
                        <div class="student-options">
                          <button
                            v-for="student in filteredStudentOptions"
                            :key="student.id"
                            type="button"
                            class="student-option"
                            @mousedown.prevent
                            @click="selectFilterStudent(student)"
                          >
                            <div class="student-option__avatar">
                              <feather-icon icon="UserIcon" size="14" />
                            </div>
                            <div class="student-option__content">
                              <span class="student-option__name">{{
                                student.name
                              }}</span>
                              <span class="student-option__meta">
                                <span v-if="student.className">{{
                                  student.className
                                }}</span>
                                <span v-if="student.sectionName">
                                  · {{ student.sectionName }}</span
                                >
                              </span>
                            </div>
                            <span
                              v-if="student.grNo"
                              class="student-option__gr"
                            >
                              GR {{ student.grNo }}
                            </span>
                          </button>
                        </div>
                      </template>
                    </template>
                  </div>
                </transition>
              </div>
            </div>

            <flat-pickr
              v-model="leavingDateRange"
              class="form-control filter-control filter-field-item"
              :config="rangeDateConfig"
              placeholder="Leaving date range"
              @on-change="onLeavingRangeChange"
            />

            <flat-pickr
              v-model="completedDateRange"
              class="form-control filter-control filter-field-item"
              :config="rangeDateConfig"
              placeholder="Completed date range"
              @on-change="onCompletedRangeChange"
            />
          </div>

          <b-button
            v-if="hasActiveFilters"
            class="action-btn-filter filter-clear-btn mx-50"
            v-b-tooltip.hover.top
            title="Clear filters"
            @click="clearFilters"
          >
            <span class="filter-clear-x" aria-hidden="true">×</span>
          </b-button>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="planner-list-table mt-1"
            :items="rows"
            :fields="fields"
            :busy="listLoading"
            show-empty
            :responsive="false"
            hover
            small
          >
            <template #empty>
              <h3 v-if="!listLoading" class="mt-1 mb-1 text-center">
                No records found
              </h3>
            </template>

            <template #table-busy>
              <tbody>
                <tr
                  v-for="n in 5"
                  :key="'skeleton-' + n"
                  class="skeleton-row"
                >
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #cell(sno)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>

            <template #cell(student)="{ item }">
              <div class="font-weight-bold" style="color: #2d1b69">
                {{ item.studentName || item.name || "—" }}
              </div>
              <small class="text-muted">
                GR {{ item.studentGrNo || item.grNo || "—" }}
              </small>
            </template>

            <template #cell(className)="{ item }">
              <b-badge variant="light-primary">
                {{ classLabel(item) }}
              </b-badge>
            </template>

            <template #cell(leavingDate)="{ item }">
              {{ formatDate(item.leavingDate) }}
            </template>

            <template #cell(reason)="{ item }">
              <span
                class="text-truncate d-inline-block"
                style="max-width: 180px"
                :title="item.reason || ''"
              >
                {{ item.reason || "—" }}
              </span>
            </template>

            <template #cell(parentInitiate)="{ item }">
              <span class="status-pill" :class="parentInitiateClass(item)">
                {{ isParentInitiatedWithdrawal(item) ? "Yes" : "No" }}
              </span>
            </template>

            <template #cell(attempts)="{ item }">
              {{ item.retentionAttempts || item.attempts || 0 }}
            </template>

            <template #cell(completed)="{ item }">
              <div class="font-weight-600" style="color: #2d1b69">
                {{ item.completedBy || item.completedByName || "—" }}
              </div>
              <small class="text-muted">
                {{ formatDateTime(item.completedOn) }}
              </small>
            </template>

            <template #cell(outcome)="{ item }">
              <span class="status-pill" :class="outcomeClass(item)">
                {{ outcomeLabel(item) }}
              </span>
            </template>

            <template #head(actions)="data">
              <div class="text-center">
                <span>{{ data.label }}</span>
              </div>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex justify-content-center align-items-center">
                <b-button
                  v-if="item.withdrawalID || item.id"
                  variant="outline-primary"
                  v-b-tooltip.hover.top
                  title="View"
                  class="action-btn-outline"
                  @click="openDetail(item.withdrawalID || item.id)"
                >
                  <feather-icon icon="EyeIcon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormInput,
  BSpinner,
  BPagination,
  BBadge,
  BRow,
  BCol,
  BTable,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import {
  withdrawalMixin,
  formatDate,
  formatDateTime,
  isParentInitiatedWithdrawal,
} from "./withdrawalShared";

export default {
  name: "RetentionHistory",
  components: {
    BCard,
    BButton,
    BFormInput,
    BSpinner,
    BPagination,
    BBadge,
    BRow,
    BCol,
    BTable,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  mixins: [withdrawalMixin],
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      listLoading: false,
      rows: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 20,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ],
      classesData: [],
      classOptions: [],
      rangeDateConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      leavingDateRange: null,
      completedDateRange: null,
      outcomeOptions: [
        { text: "All", value: null },
        { text: "Retained", value: "retained" },
        { text: "Left", value: "left" },
      ],
      parentInitiateOptions: [
        { text: "All", value: null },
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      fields: [
        { key: "sno", label: "#" },
        { key: "student", label: "Student" },
        { key: "className", label: "Class" },
        { key: "leavingDate", label: "Leaving date" },
        { key: "reason", label: "Reason" },
        { key: "parentInitiate", label: "Parent initiated" },
        { key: "attempts", label: "Attempts" },
        { key: "completed", label: "Completed" },
        { key: "outcome", label: "Outcome" },
        { key: "actions", label: "Actions" },
      ],
      filters: {
        finalOutcome: null,
        parentInitiate: null,
        sID: null,
        classID: null,
        leavingDateFrom: "",
        leavingDateTo: "",
        completedFrom: "",
        completedTo: "",
      },
      selectedStudent: null,
      studentSearchQuery: "",
      studentSearchFocused: false,
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchCache: {},
      studentSearchTimer: null,
      lastStudentSearchToken: 0,
    };
  },
  computed: {
    hasActiveFilters() {
      const f = this.filters;
      return !!(
        f.finalOutcome ||
        f.parentInitiate === true ||
        f.parentInitiate === false ||
        f.sID ||
        f.classID ||
        f.leavingDateFrom ||
        f.leavingDateTo ||
        f.completedFrom ||
        f.completedTo ||
        this.selectedStudent
      );
    },
    showStudentDropdown() {
      return this.studentSearchFocused && !this.selectedStudent;
    },
    displayedRange() {
      if (!this.totalCount) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  async created() {
    const q = this.$route.query || {};
    if (q.finalOutcome) this.filters.finalOutcome = q.finalOutcome;
    if (q.parentInitiate === "true") this.filters.parentInitiate = true;
    else if (q.parentInitiate === "false") this.filters.parentInitiate = false;
    if (q.classID) this.filters.classID = Number(q.classID);
    if (q.sID) this.filters.sID = Number(q.sID);

    try {
      await this.loadRoleSetup();
    } catch (e) {
      this.toastErr("Failed to load role setup");
    }
    await this.loadClasses();
    this.reload(true);
  },
  beforeDestroy() {
    if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
  },
  updated() {
    this.$nextTick(() => this.updateScrollbarWidth());
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),
    formatDate,
    formatDateTime,
    isParentInitiatedWithdrawal,
    parseRangeValue(val) {
      if (!val) return { from: "", to: "" };
      if (Array.isArray(val)) {
        const from = val[0] ? this.sliceDate(val[0]) : "";
        const to = val[1] ? this.sliceDate(val[1]) : from;
        return { from, to };
      }
      const raw = String(val).trim();
      if (!raw) return { from: "", to: "" };
      const parts = raw.split(/\s+to\s+/i);
      const from = this.sliceDate(parts[0] || "");
      const to = this.sliceDate(parts[1] || parts[0] || "");
      return { from, to };
    },
    onLeavingRangeChange(selectedDates) {
      const range = this.parseRangeValue(
        selectedDates && selectedDates.length
          ? selectedDates
          : this.leavingDateRange
      );
      this.filters.leavingDateFrom = range.from;
      this.filters.leavingDateTo = range.to;
      this.reload(true);
    },
    onCompletedRangeChange(selectedDates) {
      const range = this.parseRangeValue(
        selectedDates && selectedDates.length
          ? selectedDates
          : this.completedDateRange
      );
      this.filters.completedFrom = range.from;
      this.filters.completedTo = range.to;
      this.reload(true);
    },
    outcomeLabel(row) {
      const o = String(row.finalOutcome || row.outcome || "").toLowerCase();
      if (o === "retained") return "Retained";
      if (o === "left") return "Left";
      return row.outcome || row.finalOutcome || "—";
    },
    outcomeClass(row) {
      const o = String(row.finalOutcome || row.outcome || "").toLowerCase();
      if (o === "retained") return "status-pill--on_hold";
      if (o === "left") return "status-pill--completed";
      return "status-pill--draft";
    },
    parentInitiateClass(row) {
      return isParentInitiatedWithdrawal(row)
        ? "status-pill--on_hold"
        : "status-pill--draft";
    },
    classLabel(row) {
      if (row.className || row.class) {
        const cls = row.className || row.class;
        const sec = row.sectionName || row.section || "";
        return sec ? `${cls} - ${sec}` : cls;
      }
      if (!row.classID) return "—";
      const pack = (this.classesData || []).find((el) => {
        const c = el.cls || el;
        return Number(c.id) === Number(row.classID);
      });
      if (!pack) return `Class ${row.classID}`;
      const c = pack.cls || pack;
      return c.name || c.className || `Class ${row.classID}`;
    },
    async loadClasses() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        const data = Array.isArray(res)
          ? res
          : res && Array.isArray(res.data)
          ? res.data
          : [];
        this.classesData = data;
        this.classOptions = data
          .map((el) => el.cls || el)
          .filter((cls) => cls && cls.id)
          .map((cls) => ({
            id: cls.id,
            name: cls.name || cls.className || `Class ${cls.id}`,
          }));
      } catch (e) {
        console.error(e);
        this.classesData = [];
        this.classOptions = [];
      }
    },
    onFilterClassChange() {
      this.resetStudentSearchState(true);
      this.reload(true);
    },
    onPageSizeChange() {
      this.reload(true);
    },
    onStudentSearchChange() {
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700);
    },
    onStudentSearchBlur() {
      setTimeout(() => {
        this.studentSearchFocused = false;
      }, 180);
    },
    clearStudentSearchQuery() {
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },
    resetStudentSearchState(clearSelected) {
      if (clearSelected) {
        this.selectedStudent = null;
        this.filters.sID = null;
      }
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchCache = {};
      if (this.studentSearchTimer) {
        clearTimeout(this.studentSearchTimer);
        this.studentSearchTimer = null;
      }
    },
    matchesClassFilter(student) {
      if (!student || !this.filters.classID) return true;
      const studentClassID =
        student.classID ?? student.clsID ?? student.classId ?? null;
      if (studentClassID != null) {
        return Number(studentClassID) === Number(this.filters.classID);
      }
      return true;
    },
    mapStudentSearchOption(student) {
      let className = "";
      let sectionName = "";
      if (student.meta_class) {
        const parts = String(student.meta_class).split(" - ");
        className = (parts[0] || "").trim();
        sectionName = (parts[1] || "").trim();
      } else {
        className = student.className || student.cls || "";
        sectionName = student.sectionName || student.section || "";
      }
      return {
        ...student,
        id: student.id || student.sID,
        name: student.name || "Unknown Student",
        grNo: student.grNo || student.gr || "",
        className,
        sectionName,
        classID:
          student.classID ||
          student.classId ||
          student.class_current ||
          student.class_admitted ||
          null,
      };
    },
    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim().toLowerCase();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        return;
      }
      const cacheKey = `${this.filters.classID || 0}|${term}`;
      if (this.studentSearchCache[cacheKey]) {
        this.filteredStudentOptions = this.studentSearchCache[cacheKey];
        return;
      }

      const token = ++this.lastStudentSearchToken;
      this.studentSearchLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "Students/Search?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&keyword=" +
          encodeURIComponent(this.studentSearchQuery) +
          "&type=";
        if (this.filters.classID) {
          url += "&classID=" + this.filters.classID;
        }

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        if (token !== this.lastStudentSearchToken) return;

        let studentsData = [];
        if (response && Array.isArray(response.data)) studentsData = response.data;
        else if (Array.isArray(response)) studentsData = response;

        const mapped = studentsData
          .map((s) => this.mapStudentSearchOption(s))
          .filter((s) => this.matchesClassFilter(s));
        this.filteredStudentOptions = mapped;
        this.studentSearchCache[cacheKey] = mapped;
      } catch (e) {
        console.error("Student search error:", e);
        if (token === this.lastStudentSearchToken) {
          this.filteredStudentOptions = [];
        }
      } finally {
        if (token === this.lastStudentSearchToken) {
          this.studentSearchLoading = false;
        }
      }
    },
    selectFilterStudent(student) {
      this.selectedStudent = { ...student };
      this.filters.sID = student.id || student.sID || null;
      if (!this.filters.classID && student.classID) {
        this.filters.classID = Number(student.classID);
      }
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.reload(true);
    },
    clearFilterStudent() {
      this.selectedStudent = null;
      this.filters.sID = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.reload(true);
    },
    clearFilters() {
      this.filters = {
        finalOutcome: null,
        parentInitiate: null,
        sID: null,
        classID: null,
        leavingDateFrom: "",
        leavingDateTo: "",
        completedFrom: "",
        completedTo: "",
      };
      this.leavingDateRange = null;
      this.completedDateRange = null;
      this.selectedStudent = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchCache = {};
      this.reload(true);
    },
    sliceDate(val) {
      if (!val) return "";
      if (val instanceof Date && !Number.isNaN(val.getTime())) {
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, "0");
        const d = String(val.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      return String(val).slice(0, 10);
    },
    openDetail(id) {
      if (this.embedded) this.$emit("open", id);
      else {
        this.$router.push({
          name: "student-withdrawal-detail",
          params: { id: String(id) },
        });
      }
    },
    syncScroll() {
      if (this.$refs.topScrollbar && this.$refs.tableWrapper) {
        this.$refs.topScrollbar.scrollLeft = this.$refs.tableWrapper.scrollLeft;
      }
    },
    updateScrollbarWidth() {
      const wrapper = this.$refs.tableWrapper;
      const content = this.$el.querySelector(".table-scrollbar-content");
      if (wrapper && content) {
        content.style.width = `${wrapper.scrollWidth}px`;
      }
      const top = this.$refs.topScrollbar;
      if (top && !top._bound) {
        top.addEventListener("scroll", () => {
          if (this.$refs.tableWrapper) {
            this.$refs.tableWrapper.scrollLeft = top.scrollLeft;
          }
        });
        top._bound = true;
      }
    },
    async reload(resetPage) {
      if (resetPage) this.currentPage = 1;
      this.listLoading = true;
      try {
        const f = this.filters;
        const extra = {
          pageNumber: this.currentPage,
          pageSize: this.perPage,
        };
        if (f.finalOutcome) extra.finalOutcome = f.finalOutcome;
        if (f.parentInitiate === true || f.parentInitiate === false) {
          extra.parentInitiate = f.parentInitiate;
        }
        if (f.sID) extra.sID = f.sID;
        if (f.classID) extra.classID = f.classID;
        if (f.leavingDateFrom) {
          extra.leavingDateFrom = this.sliceDate(f.leavingDateFrom);
        }
        if (f.leavingDateTo) {
          extra.leavingDateTo = this.sliceDate(f.leavingDateTo);
        }
        if (f.completedFrom) {
          extra.completedFrom = this.sliceDate(f.completedFrom);
        }
        if (f.completedTo) {
          extra.completedTo = this.sliceDate(f.completedTo);
        }

        const res = await this.withdrawalGet("RetentionHistory", extra);
        const { items, total } = this.extractList(res);
        this.rows = items;
        this.totalCount = total;
      } catch (e) {
        console.error(e);
        this.rows = [];
        this.totalCount = 0;
        this.toastErr("Failed to load retention history");
      } finally {
        this.listLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";
@import "./withdrawalPanelStyles.scss";

.filter-fields-group--cols-5,
.filter-fields-group--cols-6 {
  gap: 0.5rem;
  flex-wrap: wrap;

  > .filter-field-item,
  > .filter-search-input,
  > .filter-control,
  > .flatpickr-input,
  > input.form-control {
    flex: 1 1 0;
    min-width: 0;
    width: auto !important;
    max-width: none !important;
  }

  > .filter-student-search {
    flex: 1.4 1 180px;
    min-width: 180px;
  }
}

.filter-student-search {
  position: relative;
}

.student-search-box {
  position: relative;

  &--active .student-search-input-wrap {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.student-search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 40px;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  background: #fff;
}

.student-search-icon {
  position: absolute;
  left: 0.85rem;
  color: #7367f0;
  pointer-events: none;
  z-index: 1;
}

.student-search-input {
  border: none !important;
  box-shadow: none !important;
  height: 38px !important;
  min-height: 38px !important;
  padding-left: 2.15rem !important;
  padding-right: 2rem !important;
  font-size: 0.8rem !important;
  background: transparent !important;

  &:focus {
    box-shadow: none !important;
    border-color: transparent !important;
  }
}

.student-search-clear,
.student-search-spinner {
  position: absolute;
  right: 0.65rem;
  z-index: 2;
}

.student-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f3f2f7;
  color: #6e6b7b;
  cursor: pointer;

  &:hover {
    background: #ffe2e5;
    color: #ea5455;
  }
}

.student-search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 1060;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(115, 103, 240, 0.16);
  overflow: hidden;
}

.student-search-dropdown__header {
  padding: 0.55rem 0.85rem;
  background: #faf9ff;
  border-bottom: 1px solid #f0edff;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #8a86b7;
}

.student-search-hint,
.student-search-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0.85rem;
  color: #6e6b7b;
  font-size: 0.82rem;
  text-align: center;
}

.student-search-state {
  flex-direction: column;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #f8f7ff;
    color: #7367f0;
    margin-bottom: 0.25rem;
  }

  strong {
    color: #2d1b69;
    font-size: 0.88rem;
  }
}

.student-options {
  max-height: 260px;
  overflow-y: auto;
}

.student-option {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: none;
  border-bottom: 1px solid #f3f2f7;
  background: #fff;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #faf9ff;
  }

  &:last-child {
    border-bottom: none;
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__name {
    display: block;
    font-size: 0.88rem;
    font-weight: 700;
    color: #2d1b69;
  }

  &__meta {
    display: block;
    font-size: 0.74rem;
    color: #8a86b7;
  }

  &__gr {
    margin-left: auto;
    font-size: 0.68rem;
    font-weight: 700;
    color: #7367f0;
    background: #f0edff;
    border-radius: 999px;
    padding: 0.15rem 0.5rem;
    white-space: nowrap;
  }
}

.selected-student-chip {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 40px;
  padding: 0.25rem 0.45rem 0.25rem 0.55rem;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  background: #f8f7ff;

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  &__name {
    font-size: 0.82rem;
    font-weight: 700;
    color: #2d1b69;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #6e6b7b;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: #ffe2e5;
      color: #ea5455;
    }
  }
}

.meta-tag {
  font-size: 0.68rem;
  font-weight: 600;
  color: #8a86b7;

  &--gr {
    color: #7367f0;
  }
}

.student-search-fade-enter-active,
.student-search-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.student-search-fade-enter,
.student-search-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

</style>
